import { gozokiApiWithAuth } from '../gozokiApi'

export type SensorEquipmentType = 'FRIDGE' | 'ROOM'

export type SensorStatus =
  | { state: 'OK' }
  | { state: 'OUT_OF_RANGE'; direction: 'HIGH' | 'LOW'; measured: number; min: number; max: number }
  | { state: 'INACTIVE'; lastReadingAt: string | null; thresholdMinutes: number }
  | { state: 'NO_DATA' }

export interface SensorData {
  id: number
  reference: string | null
  label: string
  description: string
  model: string | null
  fridge: {
    id: number
    reference: string | null
    label: string
    type: string
  } | null
  store: {
    id: number
    label: string
  } | null
  storeLabel: string
  type: SensorEquipmentType
  latestReading: {
    temperature: number
    humidityPercentage: number
    sensorBatteryPercentage: number
    readingTime: string
  } | null
  temperatureOffset: number
  tempMinOverride: number | null
  tempMaxOverride: number | null
  breachToleranceMinutesOverride: number | null
  inactivityThresholdMinutesOverride: number | null
  status: SensorStatus
}

// Le endpoint single `GET /sensors/:id` ne calcule pas le statut serveur ni le
// dernier relevé (réservés à la liste) : il renvoie les champs de configuration
// du capteur, dont `temperatureOffset` pour le préremplissage du formulaire.
export type SensorDetail = Omit<SensorData, 'status' | 'latestReading'>

// Fetch all sensors with their latest readings
export const fetchSensors = async (storeId?: number | null) => {
  const params = storeId && storeId !== null ? { store: storeId } : {}
  const response = await gozokiApiWithAuth.get<SensorData[]>('/sensors', { params })
  return response.data
}

export type SensorHistoryGranularity = 'raw' | 'hour' | 'day'

export interface SensorReading {
  readingTime: string
  temperature: number
  temperatureMin: number
  temperatureMax: number
  humidityPercentage: number
  sensorBatteryPercentage: number
}

export interface SensorHistoryData {
  sensorId: number
  sensorLabel: string
  equipmentLabel: string
  granularity: SensorHistoryGranularity
  readings: SensorReading[]
}

export interface FetchSensorHistoryParams {
  storeId?: number | null
  sensorIds?: number[]
  startTime?: Date
  endTime?: Date
  granularity?: SensorHistoryGranularity
}

// Fetch sensor reading history for a time period
export const fetchSensorHistory = async (params: FetchSensorHistoryParams) => {
  const queryParams: Record<string, string | number | number[]> = {}
  if (params.storeId !== null && params.storeId !== undefined) {
    queryParams.store = params.storeId
  }
  if (params.sensorIds && params.sensorIds.length > 0) {
    // Send as CSV string to avoid qs arrayLimit (default 20) on the backend
    queryParams.sensorIds = params.sensorIds.join(',')
  }
  if (params.startTime) {
    queryParams.startTime = params.startTime.toISOString()
  }
  if (params.endTime) {
    queryParams.endTime = params.endTime.toISOString()
  }
  if (params.granularity) {
    queryParams.granularity = params.granularity
  }
  const response = await gozokiApiWithAuth.get<SensorHistoryData[]>('/sensors/history', {
    params: queryParams,
  })
  return response.data
}

export interface FridgeData {
  id: number
  reference: string | null
  label: string
  type: string
  description: string
  store: {
    id: number
    checkpointStoreRef: string
    label: string
  } | null
  tempMinOverride: number | null
  tempMaxOverride: number | null
  breachToleranceMinutesOverride: number | null
  inactivityThresholdMinutesOverride: number | null
}

// Fetch a single sensor by ID
export const fetchSensor = async (id: number) => {
  const response = await gozokiApiWithAuth.get<SensorDetail>(`/sensors/${id}`)
  return response.data
}

// Fetch all fridges
export const fetchFridges = async (storeId?: number | null) => {
  const params = storeId && storeId !== null ? { store: storeId } : {}
  const response = await gozokiApiWithAuth.get<FridgeData[]>('/sensors/fridges', { params })
  return response.data
}

// Fetch a single fridge by ID
export const fetchFridge = async (id: number) => {
  const response = await gozokiApiWithAuth.get<FridgeData>(`/sensors/fridges/${id}`)
  return response.data
}
